import { Context } from "@nuxt/types";

import Api from "@/utils/api";

export type ShallowContext = Pick<Context, "$axios" | "$auth" | "$config">;

export type SearchParams = {
  type: QueryableIndex;
  search: LtkMultipleSearchResponse<QueryableIndex>;
};

export default class SearchService extends Api {
  auth: Context["$auth"];
  version: string;

  constructor({ $auth, $axios, $config }: ShallowContext) {
    super("searchService", $axios);

    this.auth = $auth;
    this.version = `${$config.version}`;
  }

  renameKeys(obj: SearchHit) {
    const keyValues = Object.keys(obj).map((key) => {
      let newKey = null;
      if (key === "objectId") {
        newKey = "objectID";
      } else {
        newKey = key;
      }
      return {
        [newKey]: obj[key as keyof SearchHit],
      };
    });
    return Object.assign({}, ...keyValues);
  }

  async search(searchParams: PostSearch[]) {
    const response = searchParams.map((item) => {
      const { type, search } = item;
      if ((type as QueryableIndex) === "suggestion") {
        return this.request<LtkSearchResponse<"suggestion">>({
          url: `/v2/suggestions/global/`,
          method: "GET",
          responseType: "json",
          params: {
            query: search?.params.query,
            limit: search?.params.limit,
          },
        });
      }
      if ((item.type as QueryableIndex) === "post") {
        return this.request<LtkSearchResponse<"post">>({
          url: `/v2/search/post/`,
          method: "POST",
          responseType: "json",
          data: {
            query: search.params?.query,
            page: search.params?.page,
            analytics: search.params?.analytics,
            limit: search.params?.limit,
          },
        });
      }
      if ((item.type as QueryableIndex) === "profile") {
        return this.request<LtkSearchResponse<"profile">>({
          url: `/v2/search/creator/`,
          method: "POST",
          responseType: "json",
          data: {
            query: search.params?.query,
            page: search.params?.page,
            analytics: search.params?.analytics,
            limit: search.params?.limit,
          },
        });
      }
      return Promise.resolve(null);
    });
    const results = (await Promise.all(response)).map((res) => {
      const hitsRekeyed = res?.data.hits.map((hit) => {
        return this.renameKeys(hit as SearchHit);
      });

      return { ...res?.data, hits: hitsRekeyed };
    });

    return { results };
  }

  /**
   * At present, the search analytics endpoint requires a user ID for analytics.
   * This is to protect against analytical manipulation from unauthenticated users attempting to
   * inflate conversion/popularity anonymously, since real-time ranking could be happening
   */
  analyticsClick(data: SearchClickEvent) {
    if (this.auth.loggedIn === false) {
      return;
    }

    return this.request({
      url: "/v1/analytics/clicks",
      method: "POST",
      responseType: "json",
      data: {
        event: data.event,
        platform: "web",
        version: this.version,
        search_string: data.query,
        content_id: data.objectId,
        content_type: data.type,
        search_record_id: data.objectId,
        search_experience: data.searchExperience,
        root_referrer: data.rootReferrer,
        index: data.index,
        click_position: data.position,
        query_id: data.queryID,
        timestamp: new Date().toISOString(),
      },
    });
  }
}
